import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Debounce } from 'app/decorators/debounce.decorator';
import { Throttle } from 'app/decorators/throttle.decorator';
import { fitAspectRatioInAspectRatio, sameAspectRatio } from 'app/shared/utils/aspectRatio.util';
import { bbDeepCopy } from 'app/shared/utils/bbDeepCopy.util';
import { Subscription, ReplaySubject } from 'rxjs';

import { BbDragEvent } from 'app/directives/bb-drag.directive';
import { BbResizeEvent } from 'app/directives/bb-resize.directive';

import { Mediaclip, MediaclipThumbnailCrop } from 'app/interfaces/mediaclip';

import { AspectRatioService, BBAspectRatio } from 'app/services/aspect-ratio.service';
import { AssetService } from 'app/services/asset.service';
import { SapiService } from 'app/services/sapi.service';

import { BasicPlayerComponent } from '../basic-player/basic-player.component';
import { Thumbnail } from '../thumbnail-selector/thumbnail-selector.component';

export interface TrimChange {
	isTrimmed: boolean;
	fromTime: number;
	toTime: number;
}

export interface ScreenshotImage {
	width: number;
	height: number;
	data?: string;
	uploadIdentifier?: string;
	src?: string;
	srcPrefix?: string;
	crop: MediaclipThumbnailCrop;
	cropsetName: string;
}

interface ResizeProps {
	zoomFactor: number;
	offset: number;
}

@Component({
	selector: 'bb-frame-select',
	templateUrl: './bb-frame-select.component.html',
	styleUrls: ['./bb-frame-select.component.scss']
})

export class BbFrameSelectComponent implements OnInit, OnChanges, OnDestroy {
	// supply mediaclip or image
	@Input() mediaclip: Mediaclip;
	@Input() image: ScreenshotImage; // src (existing, you want to crop) or just uploaded (data)

	@Input() select?: 'single' | 'range' | null; // select 1 time (fromTime) or range (fromTime and toTime), only for mediaclip
	@Input() cropSelect = false;

	@Output() trimchange: EventEmitter<TrimChange> = new EventEmitter();
	@Output() cropsetchange: EventEmitter<BBAspectRatio> = new EventEmitter();

	@ViewChild('player', { static: false }) set bp(player) {
		this.basicPlayer = player;
	}
	@ViewChild('snapshotPlayer', { static: false }) set bp2(player) {
		this.snapshotPlayer = player;
	}
	@ViewChild('timeDisplay', { static: false }) set td(timeDisplay) {
		this.timeDisplay = timeDisplay;
	}
	@ViewChild('frameStrip', { static: true }) frameStrip: ElementRef;
	@ViewChild('wrapper', { static: true }) wrapper: ElementRef;

	@ViewChild('draggerSingle', { static: true }) draggerSingle: ElementRef<HTMLDivElement>;
	@ViewChild('draggerSingleBar', { static: true }) draggerSingleBar: ElementRef<HTMLDivElement>;

	@ViewChild('cropperContainer', { static: true }) cropperContainer: ElementRef<HTMLDivElement>;
	@ViewChild('cropper', { static: false }) set cr(cropper) {
		if (cropper) {
			this.cropperRef = cropper;
			this.initCropper();
		}
	}

	heightPlayer: number;

	assetPath = '';
	lowQAssetPath = '';
	fromTime = 0;
	toTime: number;
	fromLeft = 0;
	toLeft = 0;
	initialised = false;
	dragStartWasPaused = true;
	draggingBar = false;
	fps = 25;

	subscriptions: Subscription = new Subscription();
	resizeBarSubject = new ReplaySubject<ResizeProps>(1);

	basicPlayer: BasicPlayerComponent;
	duration: number;
	currentTime = 0;

	snapshotPlayer: BasicPlayerComponent;
	timeDisplay: ElementRef;
	sizeObj: any;
	frames = [];
	frameTimes: number[];
	numFrames: number;
	stripVid: HTMLVideoElement;
	canPlaySource: boolean;
	sourceAsset: string;
	processing: boolean;

	dragging = false;
	dragLeft = 0;

	selectedCrop: MediaclipThumbnailCrop;
	activeCropset: string;
	imageCropsetUsed: string;
	cropsetOptions: { label: string; value: string }[];
	cropsets: BBAspectRatio[];
	cropperRef: ElementRef<HTMLDivElement>;
	isVertical: boolean;

	private resizeObserver: any;

	constructor(
		private assetService: AssetService,
		private sapiService: SapiService,
		private sanitizer: DomSanitizer,
		private renderer: Renderer2,
		private aspectRatioService: AspectRatioService
	) {
	}
	@HostListener('document:keydown', ['$event'])
	onKeyDown(ev: KeyboardEvent) {
		if (!this.mediaclip) {
			return;
		}

		let offset = this.sizeObj.offset;
		let zoomFactor = this.sizeObj.zoomFactor;
		if (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight') {
			ev.preventDefault();

			if (this.select === 'single') {
				this.fromTime = this.currentTime;
				this.fromTime = this.fromTime + (ev.key === 'ArrowLeft' ? -1 / this.fps : 1 / this.fps);
				this.basicPlayer?.setCurrentTime(this.fromTime);
				return;
			}

			// select === 'Range'
			const delta = 1 / this.duration / this.fps;
			const time = ev.key === 'ArrowLeft' ? -delta : delta;

			if (this.sizeObj.from !== 'right') {
				offset += time;
				offset = Math.min(offset, 1);
				offset = Math.max(offset, 0);
				if (offset !== this.sizeObj.offset) {
					zoomFactor -= time;
					zoomFactor = Math.max(zoomFactor, 0);
				}
			} else {
				zoomFactor += time;
				zoomFactor = Math.min(zoomFactor, 1 - offset);
				zoomFactor = Math.max(zoomFactor, 0);
			}

			this.resizeBarSubject.next({ zoomFactor, offset });
		}
	}

	@Throttle(500)
	onContainerResize() {
		if (this.resizeObserver === null) {
			return;
		}
		this.initCropper();
		if (this.mediaclip) {
			this.renderFrameStrip();
		}
		this.setHeightPlayer();
	}

	@Debounce(50)
	debouncePause() {
		if (this.basicPlayer?.media && !this.basicPlayer.media?.paused) {
			this.basicPlayer.media.pause();
		}
	}

	ngOnInit() {
		let sourceAspectRatio: number;
		if (this.mediaclip) {
			sourceAspectRatio = this.mediaclip.width / this.mediaclip.height;
			this.initMediaclip();
		} else {
			sourceAspectRatio = this.image.width / this.image.height;
		}

		this.isVertical = sourceAspectRatio < 1;

		this.setCropperOptions(sourceAspectRatio);

		if (this.image || this.mediaclip?.mediatype === 'video') {
			this.initResizeObserver();
		}
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges?.mediaclip) {
			if (this.mediaclip?.mediatype !== 'video') {
				this.initialised = true;
			}
			this.selectAssets();
		}
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	}

	private setHeightPlayer(): void {
		if (window.innerWidth > 1100) {
			this.heightPlayer = 450;
		} else if (window.innerWidth > 650) {
			this.heightPlayer = 350;
		} else {
			this.heightPlayer = 200;
		}
	}

	private initMediaclip(): void {
		if (this.mediaclip) {
			this.subscriptions.add(this.resizeBarSubject.subscribe((sizeObj) => {
				this.onResize(sizeObj);
			}));

			this.resizeBarSubject.next({
				zoomFactor: 1,
				offset: 0
			});

			this.sapiService.get('mediaclip', this.mediaclip.id, 'mediainfo').subscribe((techInfo: any) => {
				this.fps = Math.round(parseFloat(techInfo?.tracks?.Video?.Frame_rate)) || this.fps;
				this.canPlaySource = techInfo?.tracks?.Video?.Codec_ID?.includes('avc');
			});
		}
	}

	private setCropperOptions(sourceAspectRatio: number): void {
		this.subscriptions.add(this.aspectRatioService.aspectRatiosObs.subscribe(aspectRatios => {
			this.cropsets = aspectRatios.filter(r => !r.default);
			this.cropsetOptions = this.cropsets.map((cropset) => ({ value: cropset.name, label: cropset.label }));

			let aspectRatioVideoIndex = 0;
			if (this.image?.crop) {
				// upload or existing img
				aspectRatioVideoIndex = this.cropsets.findIndex(cs => cs.name === this.image.cropsetName);
				if (aspectRatioVideoIndex !== -1) {
					this.imageCropsetUsed = this.cropsetOptions[aspectRatioVideoIndex].value;
				}
			} else {
				// when taking screenshot: set initial crop to match video aspect ratio
				aspectRatioVideoIndex = this.cropsets.findIndex(cs => sameAspectRatio(cs.aspectratio, sourceAspectRatio));
			}

			this.activeCropset = this.cropsetOptions[aspectRatioVideoIndex !== -1 ? aspectRatioVideoIndex : 0].value;
			this.initCropper();
		}));
	}

	cropsetChanged(): void {
		this.cropsetchange.emit(this.cropsets.find(cs => cs.name === this.activeCropset));
	}

	public getScreenshotCurrentTime(): Partial<Thumbnail> {
		// image
		if (this.image?.src) {
			return {
				src: this.image.src,
				srcPrefix: this.image.srcPrefix,
				width: this.image.width.toString(),
				height: this.image.height.toString(),
				crop: this.selectedCrop,
				cropsetName: this.activeCropset
			};
		}
		if (this.image?.data) {
			return {
				data: this.image.data,
				uploadIdentifier: this.image.uploadIdentifier,
				width: this.image.width.toString(),
				height: this.image.height.toString(),
				crop: this.selectedCrop,
				cropsetName: this.activeCropset
			};
		};

		if (!this.basicPlayer) {
			return {};
		}
		// mediaclip, take a screenshot of video with canvas
		const canvas = document.createElement('canvas');
		canvas.width = (this.basicPlayer.media as HTMLVideoElement).videoWidth;
		canvas.height = (this.basicPlayer.media as HTMLVideoElement).videoHeight;

		const ctx = canvas.getContext('2d');
		ctx.drawImage((this.basicPlayer.media as HTMLVideoElement), 0, 0, canvas.width, canvas.height);

		const dataURI = canvas.toDataURL('image/jpeg');
		return {
			data: dataURI,
			width: canvas.width.toString(),
			height: canvas.height.toString(),
			crop: this.selectedCrop,
			cropsetName: this.activeCropset
		};
	}

	initResizeObserver() {
		this.resizeObserver = new ResizeObserver(entries => {
			requestAnimationFrame(() => {
				this.onContainerResize();
			});
		});

		this.resizeObserver.observe(this.wrapper.nativeElement);
	}

	onTimeChanged(time: number) {
		this.currentTime = time;
	}

	onTrimmerSingleMouseDown(event: PointerEvent) {
		if (this.select === 'single') {
			this.dragging = true;
			this.updateSingleTime(event);
		}
	}

	@Throttle(50)
	onTrimmerSingleMouseMove(event: PointerEvent) {
		this.updateSingleTime(event);
	}

	updateSingleTime(event: PointerEvent) {
		if (!this.dragging || event.offsetX <= 0) {
			return;
		}

		const parent = this.draggerSingle.nativeElement.getBoundingClientRect();
		const progress = Math.min(event.offsetX / (parent.width), 1);
		const newTime = progress * this.duration;

		this.dragLeft = progress * 100;
		this.basicPlayer?.setCurrentTime(newTime);
	}

	onTrimmerSingleMouseUp() {
		this.dragging = false;
	}

	onDraggingRange() {
		// play media so we hear scrubbing
		if (this.dragStartWasPaused && this.basicPlayer?.media?.paused && this.basicPlayer.media.volume !== 0) {
			this.basicPlayer.media.play();
			this.debouncePause();
		}
	}

	onDragStartRange() {
		this.draggingBar = true;
	}

	onDragEndRange() {
		this.draggingBar = false;
	}

	log(msg: any) {
		console.warn(msg);
	}

	onResize(sizeObj) {
		const lastFrom = this.sizeObj?.from;
		this.sizeObj = sizeObj;
		if (!this.sizeObj.from) {
			this.sizeObj.from = lastFrom;
		}

		const duration = this.duration ?? this.mediaclip.length;

		// round times to nearest frame time
		this.fromTime = Math.round(sizeObj.offset * duration * this.fps) / this.fps;
		this.toTime = Math.min(duration, Math.round((sizeObj.offset + sizeObj.zoomFactor) * duration * this.fps) / this.fps);

		this.trimchange.emit({
			isTrimmed: sizeObj.offset !== 0 || sizeObj.zoomFactor !== 1,
			fromTime: this.fromTime,
			toTime: this.toTime,
		});

		const tdEl = this.timeDisplay?.nativeElement;
		if (tdEl) {
			const fromEl = tdEl.querySelector('.from-time');
			const toEl = tdEl.querySelector('.to-time');
			this.fromLeft = sizeObj.offset * tdEl.offsetWidth > fromEl.offsetWidth ? -fromEl.offsetWidth : 10;
			this.toLeft = tdEl.offsetWidth - ((sizeObj.offset + sizeObj.zoomFactor) * tdEl.offsetWidth) > toEl.offsetWidth ? 0 : -toEl.offsetWidth - 10;
		}

		if (this.basicPlayer) {
			this.basicPlayer.setCurrentTime(sizeObj.from === 'right' ? this.toTime : this.fromTime);
		}
	}

	onTimecodeChange(fromTo: 'from' | 'to', time: number) {
		const factor = time / this.duration;
		// const oldDuration = this.toTime - this.fromTime;
		const newOffset = fromTo === 'from' ? factor : this.sizeObj.offset;
		const newZoom = fromTo === 'from' ? this.sizeObj.zoomFactor + (this.sizeObj.offset - factor) : factor - this.sizeObj.offset;

		this.resizeBarSubject.next({
			zoomFactor: newZoom,
			offset: newOffset
		});
	}

	onDurationChange(duration: number) {
		if (this.duration !== duration && !isNaN(duration)) {
			this.duration = duration;
			this.onResize(this.sizeObj);

			if (this.mediaclip.mediatype !== 'video') {
				if (this.basicPlayer?.media) {
					this.basicPlayer.media.volume = 1;
				}
			} else {
				this.renderFrameStrip();
			}
		}
	}

	renderFrameStrip() {
		// takes snapshots from the video and renders them in canvases
		const tdEl = this.frameStrip?.nativeElement;
		if (
			this.mediaclip?.mediatype !== 'video' || !this.duration || !tdEl?.offsetWidth || !this.snapshotPlayer
			|| !(this.snapshotPlayer.media instanceof HTMLVideoElement)
		) {
			return;
		}

		if (!this.initialised) {
			const media = this.stripVid = this.snapshotPlayer?.media;

			setTimeout(() => {
				if (!media) {
					return;
				}
				const canvas = document.createElement('canvas');
				canvas.width = media.videoWidth;
				canvas.height = media.videoHeight;
				const ctx = canvas.getContext('2d');

				if (!media.videoWidth) {
					setTimeout(() => {
						// try again if player did not render completely yet
						this.renderFrameStrip();
					}, 500);
					return;
				}

				// wait on the seeked event to take a snapshot of the video element
				media.addEventListener('seeked', () => {
					if (this.frameTimes.length) {
						ctx.drawImage(media, 0, 0, canvas.width, canvas.height);

						const dataURI = canvas.toDataURL('image/jpeg');
						setTimeout(() => {
							this.frames.push({ data: this.sanitizer.bypassSecurityTrustUrl(dataURI) });
							const t = this.frameTimes.shift();
							if (t) {
								media.currentTime = t;
							}
							if (this.frameTimes.length === 0) {
								setTimeout(() => {
									this.initialised = true;
								}, 50);
							}
						});
					}
				});

				const aspectRatio = media.videoWidth / media.videoHeight;
				const frameWidth = aspectRatio * 50;

				const numFrames = Math.ceil(tdEl.offsetWidth / frameWidth);
				if (this.numFrames === numFrames || isNaN(numFrames)) {
					return;
				}
				this.frames = [];

				this.frameTimes = [];
				this.numFrames = numFrames;
				// seek to first frame to start the seek routine
				media.currentTime = this.numFrames ? this.duration / this.numFrames / 2 : 0;

				// start at 1 because the first frame is from the first seek
				for (let i = 1; i <= this.numFrames; i++) {
					const frameDuration = this.duration / this.numFrames;
					const time = frameDuration * i + (frameDuration / 2);
					this.frameTimes.push(time);
				}
			}, 100);
		}
	}

	selectAssets() {
		if (!this.mediaclip || !this.mediaclip.assets || !this.mediaclip.assets.length) {
			return '';
		}

		const filteredAssets: any[] = [];

		this.mediaclip.assets.forEach((asset: any) => {
			if (asset.mediatype &&
				!asset.mediatype.includes('HLS') &&
				!asset.mediatype.includes('TIMELINE') && (
				asset.mediatype.startsWith('MP4_') ||
				asset.mediatype.startsWith('AAC') ||
				asset.mediatype.startsWith('MP3')
			)) {
				filteredAssets.push(asset);
			}
		});
		filteredAssets.sort((a, b) => Number(b.bandwidth - a.bandwidth));

		if (filteredAssets[0]?.src) {
			this.assetPath = this.assetService.getAssetUrl(this.mediaclip, filteredAssets[0].src);
			this.sourceAsset = this.assetService.getAssetUrl(this.mediaclip, this.mediaclip?.src, true);
		}
		const lowQ = filteredAssets[filteredAssets.length - 1];
		if (lowQ?.src) {
			this.lowQAssetPath = this.assetService.getAssetUrl(this.mediaclip, lowQ.src);
		}
	}

	// set initial crop box with correct aspect ratio
	private initCropper() {
		if (!this.cropperRef || !this.activeCropset) {
			return;
		}

		const videoBox = this.cropperContainer.nativeElement.getBoundingClientRect();
		const currentCropset = this.cropsets.find(cr => cr.name === this.activeCropset);
		let imageCropped = false;

		const cropDimensions = fitAspectRatioInAspectRatio(currentCropset?.aspectratio, (videoBox.width / videoBox.height));
		const { heightRatio, widthRatio, leftRatio, topRatio } = cropDimensions;

		// values in px for rendering box
		let height = videoBox.height * heightRatio;
		let width = videoBox.width * widthRatio;
		let left = videoBox.width * leftRatio;
		let top  = videoBox.height * topRatio;

		// loading an already cropped image and same cropset
		if (this.image?.crop && this.activeCropset === this.imageCropsetUsed) {
			if (this.image.crop.x !== null && this.image.crop.y !== null && this.image.crop.width !== null && this.image.crop.height !== null) {
				height = this.image.crop.height * videoBox.height;
				width = this.image.crop.width * videoBox.width;
				left = this.image.crop.x * videoBox.width;
				top = this.image.crop.y * videoBox.height;
				this.selectedCrop = this.image.crop;
				imageCropped = true;
			}
		}

		// render initial box
		this.renderer.setStyle(this.cropperRef.nativeElement, 'width', width + 'px');
		this.renderer.setStyle(this.cropperRef.nativeElement, 'height', height + 'px');
		this.renderer.setStyle(this.cropperRef.nativeElement, 'left', left + 'px');
		this.renderer.setStyle(this.cropperRef.nativeElement, 'top', top + 'px');

		if (!imageCropped) {
			this.selectedCrop = {
				width: widthRatio,
				height: heightRatio,
				x: leftRatio,
				y: topRatio,
				main: true
			};
		}
	}

	onCropResizeEnd(position: BbDragEvent | BbResizeEvent): void {
		// BbDragEvent: check if drag event, has left (x) and top (y) and update selectedCrop
		// width/height/aspectratio remains same for a drag
		if (position.type === 'dragended') {
			const container = this.cropperContainer.nativeElement.getBoundingClientRect();
			this.selectedCrop = bbDeepCopy(this.selectedCrop);
			this.selectedCrop.x = position.left / container.width;
			this.selectedCrop.y = position.top / container.height;
			return;
		}

		// BbResizeEvent: image was resized: update all dimensions
		if (position.type === 'resizeended') {
			this.selectedCrop = {
				width: position.widthRatio,
				height: position.heightRatio,
				x: position.leftRatio,
				y: position.topRatio,
				main: true
			};
		}
	}
}
